/**
 * Cấu hình → Ưu đãi (KH-GĐ2 6.2.1, 6.2.5; kế hoạch gd2-6-uu-dai.md mục 7). Chỉ chủ tiệm, công tắc "Ưu đãi".
 *
 * Một màn, 5 ô điền sẵn theo mẫu: (1) kiểu + mức, (2) giảm tối đa, (3) dịch vụ áp dụng, (4) hạn dùng, (5) tổng suất.
 * Nút chính "Lưu". Dưới là 5 con số. Khách thấy ưu đãi khi quét mã QR đặt lịch của tiệm (không làm QR mới).
 * Đã có khách nhận thì chỉ gia hạn / đổi tổng suất / tạm dừng (máy chủ chặn phần còn lại).
 */
import { type DieuKhoanUuDai, moTaUuDai } from '@sapa/shared'
import { useEffect, useState } from 'react'
import {
  type Api,
  type LuuUuDai,
  type ManUuDai,
  ngayVn,
  type ServiceOption,
  tenDichVu,
  themNgay,
  tienVnd,
  uuDaiApi,
} from './api.ts'
import { loiCua, ngayViet, OTien } from './GoiVi.tsx'
import { U } from './text/uu-dai.ts'
import { ThongBao, Xuong } from './ui.tsx'

type Kieu = DieuKhoanUuDai['kieu']

interface Nhap {
  kieu: Kieu
  /** Phần trăm dạng chữ số nguyên ("20"). */
  phanTram: string
  soTien: string
  toiDa: string
  dichVu: string[]
  denNgay: string
  tongSuat: string
}

function tuMan(m: ManUuDai): Nhap {
  const ct = m.chuongTrinh
  const dk = ct?.dieuKhoan ?? { ...m.mau, giaTriVnd: null }
  return {
    kieu: dk.kieu,
    phanTram: dk.giaTriBp === null ? '' : String(Math.trunc(dk.giaTriBp / 100)),
    soTien: dk.giaTriVnd ?? '',
    toiDa: dk.toiDaVnd ?? '',
    dichVu: dk.dichVu,
    denNgay: ct?.denNgay ?? m.mau.denNgay,
    tongSuat: String(ct?.tongSuat ?? m.mau.tongSuat),
  }
}

export function TheUuDaiCauHinh({ api }: { api: Api }) {
  const [man, setMan] = useState<ManUuDai | null>(null)
  const [dv, setDv] = useState<ServiceOption[]>([])
  const [n, setN] = useState<Nhap | null>(null)
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [bao, setBao] = useState<string | null>(null)

  useEffect(() => {
    void Promise.all([uuDaiApi.doc(), api.services()])
      .then(([m, s]) => {
        setMan(m)
        setN(tuMan(m))
        setDv(s.services)
      })
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api])

  if (man === null || n === null)
    return loi === null ? <Xuong cao={200} /> : <ThongBao loai="loi">{loi}</ThongBao>
  const ct = man.chuongTrinh
  const khoa = ct !== null && ct.daPhat > 0
  const pt = Number.parseInt(n.phanTram, 10)
  const hopLe =
    (n.kieu !== 'phan_tram' || (pt >= 1 && pt <= 100)) &&
    (n.kieu !== 'so_tien' || (n.soTien !== '' && BigInt(n.soTien) >= 1000n)) &&
    (n.kieu !== 'tang_dich_vu' || n.dichVu.length === 1) &&
    /^\d{4}-\d{2}-\d{2}$/.test(n.denNgay) &&
    Number.parseInt(n.tongSuat, 10) >= 1

  // Câu mô tả khách sẽ thấy — cùng hàm với máy chủ.
  const dkXem: DieuKhoanUuDai = {
    kieu: n.kieu,
    giaTriBp: n.kieu === 'phan_tram' && pt > 0 ? pt * 100 : null,
    giaTriVnd: n.kieu === 'so_tien' && n.soTien !== '' ? BigInt(n.soTien) : null,
    toiDaVnd: n.kieu === 'phan_tram' && n.toiDa !== '' ? BigInt(n.toiDa) : null,
    dichVu: n.dichVu,
  }
  const tenDv = (id: string) => tenDichVu(dv.find((s) => s.id === id)?.label ?? '')
  // Nút Lưu xám thì nói lý do (ui-critic GĐ3).
  const lyDoChuaLuu = hopLe
    ? null
    : n.kieu === 'phan_tram' && !(pt >= 1 && pt <= 100)
      ? U.canPhanTram
      : n.kieu === 'so_tien' && !(n.soTien !== '' && BigInt(n.soTien) >= 1000n)
        ? U.canSoTien
        : n.kieu === 'tang_dich_vu' && n.dichVu.length !== 1
          ? U.canDvTang
          : U.canSuat
  const moTa =
    n.kieu === 'phan_tram' && !(pt > 0)
      ? ''
      : moTaUuDai(dkXem, n.dichVu.length === 1 ? tenDv(n.dichVu[0] ?? '') : undefined)

  async function luu(them: Partial<LuuUuDai> = {}) {
    if (n === null) return
    setDang(true)
    setLoi(null)
    try {
      const than: LuuUuDai = khoa
        ? { denNgay: n.denNgay, tongSuat: Number.parseInt(n.tongSuat, 10) }
        : {
            kieu: n.kieu,
            giaTriBp: n.kieu === 'phan_tram' ? pt * 100 : null,
            giaTriVnd: n.kieu === 'so_tien' ? n.soTien : null,
            toiDaVnd: n.kieu === 'phan_tram' && n.toiDa !== '' ? n.toiDa : null,
            dichVu: n.dichVu,
            denNgay: n.denNgay,
            tongSuat: Number.parseInt(n.tongSuat, 10),
          }
      const m = await uuDaiApi.luu({ ...(ct === null ? {} : { id: ct.id }), ...than, ...them })
      setMan(m)
      setN(tuMan(m))
      setBao(U.daLuu)
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  const doiDv = (id: string) => {
    if (n.kieu === 'tang_dich_vu') setN({ ...n, dichVu: [id] })
    else
      setN({
        ...n,
        dichVu: n.dichVu.includes(id) ? n.dichVu.filter((x) => x !== id) : [...n.dichVu, id],
      })
  }
  const so = man.soLieu

  return (
    <>
      <section className="the the-dem the-khoi">
        <h2>{U.tieuDe}</h2>
        <p className="chu-phu">{U.giaiThich}</p>
        {bao !== null && (
          <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
            {bao}
          </ThongBao>
        )}
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {ct !== null && (
          <p>
            <span className={ct.dangBat && ct.conNhan ? 'tt-uu-dai' : 'tt-uu-dai dung'}>
              {ct.dangBat
                ? ct.conNhan
                  ? U.dangChay(ct.daPhat, ct.tongSuat)
                  : U.hetNhan
                : U.tamDung}
            </span>
          </p>
        )}
        {khoa && <ThongBao loai="canh-bao">{U.daCoKhach}</ThongBao>}

        {/* (1) Kiểu + mức */}
        <div className="truong">
          <span className="nhan">{U.kieu}</span>
          <div className="hang-chip">
            {(['phan_tram', 'so_tien', 'tang_dich_vu'] as const).map((k) => (
              <button
                key={k}
                type="button"
                disabled={khoa}
                aria-pressed={n.kieu === k}
                className={`chip ${n.kieu === k ? 'dang-chon' : ''}`}
                onClick={() =>
                  setN({
                    ...n,
                    kieu: k,
                    dichVu: k === 'tang_dich_vu' ? n.dichVu.slice(0, 1) : n.dichVu,
                  })
                }
              >
                {U.tenKieu[k]}
              </button>
            ))}
          </div>
        </div>
        {n.kieu === 'phan_tram' && (
          <label className="truong o-co-don-vi" htmlFor="ud-phan-tram">
            <span className="nhan">{U.phanTram}</span>
            <input
              id="ud-phan-tram"
              className="o-nhap"
              inputMode="numeric"
              disabled={khoa}
              maxLength={3}
              value={n.phanTram}
              onChange={(e) => setN({ ...n, phanTram: e.target.value.replace(/\D/g, '') })}
            />
            <span className="don-vi" aria-hidden="true">
              %
            </span>
          </label>
        )}
        {n.kieu === 'so_tien' && (
          <label className="truong" htmlFor="ud-so-tien">
            <span className="nhan">{U.soTien}</span>
            <OTien
              id="ud-so-tien"
              khoa={khoa}
              giaTri={n.soTien}
              onDoi={(s) => !khoa && setN({ ...n, soTien: s })}
            />
          </label>
        )}
        {/* (2) Giảm tối đa */}
        {n.kieu === 'phan_tram' && (
          <label className="truong" htmlFor="ud-toi-da">
            <span className="nhan">{U.toiDa}</span>
            <OTien
              id="ud-toi-da"
              khoa={khoa}
              giaTri={n.toiDa}
              onDoi={(s) => !khoa && setN({ ...n, toiDa: s })}
              goiY={U.toiDaGoiY}
            />
          </label>
        )}
        {/* (3) Dịch vụ áp dụng */}
        <div className="truong">
          <span className="nhan">{n.kieu === 'tang_dich_vu' ? U.dichVuTang : U.dichVu}</span>
          <div className="hang-chip">
            {n.kieu !== 'tang_dich_vu' && (
              <button
                type="button"
                disabled={khoa}
                aria-pressed={n.dichVu.length === 0}
                className={`chip ${n.dichVu.length === 0 ? 'dang-chon' : ''}`}
                onClick={() => setN({ ...n, dichVu: [] })}
              >
                {U.moiDichVu}
              </button>
            )}
            {dv.map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={khoa}
                aria-pressed={n.dichVu.includes(s.id)}
                className={`chip ${n.dichVu.includes(s.id) ? 'dang-chon' : ''}`}
                onClick={() => doiDv(s.id)}
              >
                {tenDichVu(s.label)}
              </button>
            ))}
          </div>
        </div>
        {/* (4) Hạn dùng */}
        {/* Chip số ngày thay ô ngày của trình duyệt (hiện kiểu Mỹ "10/29/2026" — ui-critic v11 đã chê). */}
        <div className="truong">
          <span className="nhan">{U.han}</span>
          <div className="hang-chip">
            {[30, 60, 90].map((so) => {
              const den = themNgay(ngayVn(), so)
              return (
                <button
                  key={so}
                  type="button"
                  aria-pressed={n.denNgay === den}
                  className={`chip ${n.denNgay === den ? 'dang-chon' : ''}`}
                  onClick={() => setN({ ...n, denNgay: den })}
                >
                  {U.soNgay(so)}
                </button>
              )
            })}
          </div>
          <p className="chu-phu">{U.toiNgay(ngayViet(n.denNgay))}</p>
        </div>
        {/* (5) Tổng số suất */}
        <label className="truong" htmlFor="ud-suat">
          <span className="nhan">{U.suat}</span>
          <input
            id="ud-suat"
            className="o-nhap"
            inputMode="numeric"
            maxLength={6}
            value={n.tongSuat}
            onChange={(e) => setN({ ...n, tongSuat: e.target.value.replace(/\D/g, '') })}
          />
        </label>
        {moTa !== '' && <p className="xem-truoc-uu-dai">{U.khachThay(moTa)}</p>}
        <button
          type="button"
          className="nut nut-chinh nut-lon nut-rong"
          style={{ marginTop: 12 }}
          disabled={dang || !hopLe}
          onClick={() => void luu()}
        >
          {ct === null ? U.batDau : U.luu}
        </button>
        {lyDoChuaLuu !== null && <p className="chu-phu">{lyDoChuaLuu}</p>}
        {ct !== null && (
          <button
            type="button"
            className="nut nut-vien nut-rong"
            style={{ marginTop: 8 }}
            disabled={dang}
            onClick={() => void luu({ dangBat: !ct.dangBat })}
          >
            {ct.dangBat ? U.dung : U.chayLai}
          </button>
        )}
        <p className="chu-phu">{U.qr}</p>
      </section>

      {so !== null && (
        <section className="the the-dem so-to">
          <span className="nhan-muc">{U.soLieu}</span>
          <ul className="ds-so">
            <li>
              <span>{U.daNhan}</span>
              <b>{so.daNhan}</b>
            </li>
            <li>
              <span>{U.daDung}</span>
              <b>{so.daDung}</b>
            </li>
            <li>
              <span>{U.tongThu}</span>
              <b>{tienVnd(so.tongThuVnd)}</b>
            </li>
            <li>
              <span>{U.tongGiam}</span>
              <b>{tienVnd(so.tongGiamVnd)}</b>
            </li>
            <li>
              <span>{U.quayLai}</span>
              <b>{so.quayLai}</b>
            </li>
          </ul>
        </section>
      )}
    </>
  )
}
